import {FC, ReactNode, useState, useEffect} from 'react'
import {api, BedWash, UpcomingWash, ApiError} from '../../services/api'
import {useToast} from '../../components/Toast'
import {Spinner} from '../../components/Spinner'
import {ACCENT, INK, MUTED, Empty, btnStyle, fmt, fmtDate, money} from './ui'

const PAGE_SIZE = 10

/** Collapsible card with a count badge — one per queue/upcoming/completed group. */
const Accordion: FC<{title: string; subtitle?: string; count: number; defaultOpen?: boolean; children: ReactNode}> = ({
  title,
  subtitle,
  count,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', overflow: 'hidden', marginBottom: 18}}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '18px 22px', borderBottom: open ? '1px solid #f1f3f7' : 'none', display: 'flex', alignItems: 'center', gap: 12}}
      >
        <div style={{flex: 1, minWidth: 0}}>
          <h2 style={{fontSize: 16, fontWeight: 800, color: INK, margin: 0, display: 'flex', alignItems: 'center', gap: 8}}>
            {title}
            <span style={{fontSize: 12, fontWeight: 700, color: MUTED, background: '#f1f3f7', borderRadius: 999, padding: '2px 9px'}}>{count}</span>
          </h2>
          {subtitle && <p style={{fontSize: 13, color: MUTED, margin: '4px 0 0'}}>{subtitle}</p>}
        </div>
        <span style={{fontSize: 20, color: MUTED, display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s'}}>›</span>
      </button>
      {open && children}
    </div>
  )
}

/** Renders a page of `items` (10 at a time) with Prev/Next controls. */
function PagedList<T>({items, renderItem}: {items: T[]; renderItem: (item: T) => ReactNode}): JSX.Element {
  const [page, setPage] = useState(0)
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const cur = Math.min(page, totalPages - 1)
  const slice = items.slice(cur * PAGE_SIZE, cur * PAGE_SIZE + PAGE_SIZE)
  const pagerBtn = (disabled: boolean): React.CSSProperties => ({
    fontWeight: 700, fontSize: 13, color: disabled ? '#c2c6d2' : INK, background: 'none', border: 'none',
    cursor: disabled ? 'default' : 'pointer', padding: '4px 8px',
  })
  return (
    <>
      {slice.map(renderItem)}
      {totalPages > 1 && (
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #f1f3f7'}}>
          <button disabled={cur === 0} onClick={() => setPage(cur - 1)} style={pagerBtn(cur === 0)}>‹ Prev</button>
          <span style={{fontSize: 13, color: MUTED}}>Page {cur + 1} of {totalPages}</span>
          <button disabled={cur >= totalPages - 1} onClick={() => setPage(cur + 1)} style={pagerBtn(cur >= totalPages - 1)}>Next ›</button>
        </div>
      )}
    </>
  )
}

/** The bed-wash queue — Start → Done — with upcoming + completed groups. */
const CleanerQueuePage: FC = () => {
  const toast = useToast()
  const [queue, setQueue] = useState<BedWash[]>([])
  const [upcoming, setUpcoming] = useState<UpcomingWash[]>([])
  const [completed, setCompleted] = useState<BedWash[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const loadCompleted = () =>
    api.bedWashHistory().catch(() => []).then((list) => setCompleted(list.filter((w) => w.status === 'DONE')))

  useEffect(() => {
    api.bedWashQueue().catch(() => []).then(setQueue).finally(() => setLoading(false))
    api.bedWashUpcoming().catch(() => []).then(setUpcoming)
    void loadCompleted()
  }, [])

  const start = async (w: BedWash) => {
    setBusyId(w.id)
    try {
      const updated = await api.markBedWashInProgress(w.id)
      setQueue((list) => list.map((x) => (x.id === w.id ? updated : x)))
      toast.success(`Started Bed ${w.bedCode ?? ''}.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update')
    } finally {
      setBusyId(null)
    }
  }

  const markDone = async (w: BedWash) => {
    setBusyId(w.id)
    try {
      await api.markBedWashDone(w.id)
      setQueue((list) => list.filter((x) => x.id !== w.id))
      void loadCompleted()
      toast.success(`Bed ${w.bedCode ?? ''} marked washed. ${money(w.amount)} added to your tab.`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not update')
    } finally {
      setBusyId(null)
    }
  }

  const renderQueueItem = (w: BedWash): ReactNode => {
    const inProgress = w.status === 'IN_PROGRESS'
    return (
      <div key={w.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderTop: '1px solid #f1f3f7', flexWrap: 'wrap'}}>
        <div style={{minWidth: 0}}>
          <div style={{fontSize: 16, fontWeight: 800, color: INK, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
            Bed {w.bedCode ?? '—'}
            <span style={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderRadius: 999, padding: '3px 9px', color: w.source === 'AUTO' ? '#b8770a' : '#1f9d5b', background: w.source === 'AUTO' ? 'rgba(255,168,0,0.15)' : 'rgba(3,149,64,0.12)'}}>
              {w.source === 'AUTO' ? '6-week due' : 'Requested'}
            </span>
            {inProgress && (
              <span style={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderRadius: 999, padding: '3px 9px', color: '#2b4eff', background: 'rgba(43,78,255,0.12)'}}>In progress</span>
            )}
          </div>
          <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>
            {w.renter ? `${w.renter.firstName} ${w.renter.lastName}` : 'Renter'} · {fmt(w.createdAt)} · {money(w.amount)}
          </div>
        </div>
        <div style={{display: 'flex', gap: 8}}>
          {!inProgress && (
            <button onClick={() => start(w)} disabled={busyId === w.id} style={btnStyle('#eef1f6', INK, busyId === w.id)}>
              {busyId === w.id ? <Spinner size={13} color={INK} /> : 'Start'}
            </button>
          )}
          <button onClick={() => markDone(w)} disabled={busyId === w.id} style={btnStyle(ACCENT, '#fff', busyId === w.id)}>
            {busyId === w.id ? <Spinner size={13} /> : 'Mark done'}
          </button>
        </div>
      </div>
    )
  }

  const renderUpcomingItem = (u: UpcomingWash): ReactNode => (
    <div key={`${u.bedCode}-${u.nextWashAt}`} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderTop: '1px solid #f1f3f7', flexWrap: 'wrap'}}>
      <div style={{minWidth: 0}}>
        <div style={{fontSize: 16, fontWeight: 800, color: INK}}>Bed {u.bedCode ?? '—'}</div>
        <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>{u.renterName ?? 'Renter'}</div>
      </div>
      <div style={{textAlign: 'right'}}>
        <div style={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#b8770a'}}>Next wash</div>
        <div style={{fontSize: 14, fontWeight: 700, color: INK, marginTop: 1}}>{fmtDate(u.nextWashAt)}</div>
      </div>
    </div>
  )

  const renderCompletedItem = (w: BedWash): ReactNode => (
    <div key={w.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderTop: '1px solid #f1f3f7', flexWrap: 'wrap'}}>
      <div style={{minWidth: 0}}>
        <div style={{fontSize: 16, fontWeight: 800, color: INK, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
          Bed {w.bedCode ?? '—'}
          <span style={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderRadius: 999, padding: '3px 9px', color: '#1f9d5b', background: 'rgba(3,149,64,0.12)'}}>Done</span>
        </div>
        <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>
          {w.renter ? `${w.renter.firstName} ${w.renter.lastName}` : 'Renter'} · {fmt(w.completedAt ?? w.createdAt)} · {money(w.amount)}
        </div>
      </div>
      <span style={{fontSize: 20}}>✅</span>
    </div>
  )

  return (
    <>
      <Accordion title='Bed-wash queue' subtitle='Beds waiting to be washed — requested by a renter or due on the 6-week schedule.' count={queue.length} defaultOpen>
        {loading ? (
          <Empty>Loading…</Empty>
        ) : queue.length === 0 ? (
          <Empty>Nothing in the queue right now. 🎉</Empty>
        ) : (
          <PagedList items={queue} renderItem={renderQueueItem} />
        )}
      </Accordion>

      <Accordion title='Upcoming washes' subtitle='Scheduled on the 6-week hygiene clock — not due yet. Each is charged $5 to the renter’s card and lands in the queue when its date arrives.' count={upcoming.length}>
        {upcoming.length === 0 ? (
          <Empty>Nothing scheduled yet.</Empty>
        ) : (
          <PagedList items={upcoming} renderItem={renderUpcomingItem} />
        )}
      </Accordion>

      <Accordion title='Completed washes' subtitle='Jobs you’ve finished — most recent first. Each added its fee to your earnings tab.' count={completed.length}>
        {completed.length === 0 ? (
          <Empty>No completed washes yet.</Empty>
        ) : (
          <PagedList items={completed} renderItem={renderCompletedItem} />
        )}
      </Accordion>
    </>
  )
}

export {CleanerQueuePage}
